import { authenticatedFetch } from "@lumi/data";
import { useState, type ReactElement } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import type { Collection, MapRevealConfig } from "../types/collections";

interface MapRevealConfigEditorProps {
  readonly collection: Collection;
  readonly onSuccess: () => void;
}

/**
 * MapRevealConfigEditor - Editor for Map Reveal section configuration
 */
// eslint-disable-next-line max-lines-per-function
export function MapRevealConfigEditor({ collection, onSuccess }: MapRevealConfigEditorProps): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const [title, setTitle] = useState(collection.config?.title ?? "");
  const [subtitle, setSubtitle] = useState(collection.config?.subtitle ?? "");
  const [buttonText, setButtonText] = useState(collection.config?.buttonText ?? "");
  const [buttonLink, setButtonLink] = useState(collection.config?.buttonLink ?? "");
  const [vignetteOpacity, setVignetteOpacity] = useState((collection.config?.vignetteOpacity ?? 0.2).toString());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const handleSave = async () => {
    if (!accessToken) return;

    setSaving(true);
    setMessage(null);

    try {
      const config: MapRevealConfig = {
        title: title || undefined,
        subtitle: subtitle || undefined,
        buttonText: buttonText || undefined,
        buttonLink: buttonLink || undefined,
        vignetteOpacity: Number.parseFloat(vignetteOpacity) || 0.2,
      };

      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collection.key}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setMessage({ type: "success", text: "Map Reveal settings saved successfully" });
      onSuccess();
    } catch (err) {
      console.error("❌ Failed to save config:", err);
      setMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">Map Reveal Configuration</h3>
        <p className="text-xs text-neutral-400">
          Configure the visual presentation and call-to-action for this map section
        </p>
      </div>

      {message && (
        <div
          className={`p-3 border rounded ${
            message.type === "success" ? "bg-green-900/20 border-green-800" : "bg-red-900/20 border-red-800"
          }`}
        >
          <p className={`text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}>{message.text}</p>
        </div>
      )}

      <div className="space-y-4 bg-none p-6">
        <div className="space-y-2">
          <Label htmlFor="map-title">Title</Label>
          <Input
            id="map-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Explore World Cinema"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">Main heading displayed above the map</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="map-subtitle">Subtitle</Label>
          <Input
            id="map-subtitle"
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Discover films by location"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">Secondary text describing the map feature</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="map-button-text">Button Text</Label>
          <Input
            id="map-button-text"
            type="text"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            placeholder="Browse Map"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">Text shown on the call-to-action button</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="map-button-link">Button Link</Label>
          <Input
            id="map-button-link"
            type="text"
            value={buttonLink}
            onChange={(e) => setButtonLink(e.target.value)}
            placeholder="/home/browse"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">URL where the button should navigate</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="map-vignette">Vignette Opacity (0-1)</Label>
          <Input
            id="map-vignette"
            type="number"
            value={vignetteOpacity}
            onChange={(e) => setVignetteOpacity(e.target.value)}
            placeholder="0.2"
            min="0"
            max="1"
            step="0.1"
            className="bg-transparent"
          />
          <p className="text-xs text-neutral-500">
            How transparent the vignette becomes on hover (0 = invisible, 1 = opaque)
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={() => void handleSave()} disabled={saving} variant="outline" size="sm">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>
    </div>
  );
}
